import React, { useState, useEffect, useMemo } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import useIsomorphicStyles from 'isomorphic-style-loader/useStyles';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';


// Material UI and Components Imports
import Tooltip from '@material-ui/core/Tooltip';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import TwoLabelSwitch from 'components/TwoLabelSwitch';
import Icon from 'components/Icon';
import {
  setConvertedIngredients,
  switchCurrentMeasurementSystem,
  toggleConversionEditOpen,
} from 'actions/conversion';

import { COLOURS } from 'constants/index';
import history from 'services/history';

const ResponsiveTooltip = props => {
  const isMobile = useMediaQuery('(hover: none) and (pointer: coarse)');
  const [open, setOpen] = useState(false);
  const { children, ...rest } = props;
  const handleClick = () => {
    setOpen(!open);
  };

  if (isMobile) {
    return (
      <button style={{ display: 'flex' }} onClick={handleClick}>
        <Tooltip {...rest} open={open} onClose={() => setOpen(false)}>
          {children}
        </Tooltip>
      </button>
    );
  }

  return <Tooltip {...rest}>{children}</Tooltip>;
};

ResponsiveTooltip.propTypes = {
  children: PropTypes.node.isRequired,
};

const CONVERT_TIMEOUT = 30;

const RecipeConvertStyles = require('./RecipeConvert.scss');

const CONVERT_RECIPE_USING_QUEUE = gql`
  mutation ConvertRecipeUsingQueue($id: String!, $prompt: String, $model: String) {
    ConvertRecipeUsingQueue(id: $id, prompt: $prompt, model: $model)
  }
`;

const GET_CONVERTED_RECIPE = gql`
  query GetConvertedRecipe($id: String!) {
    GetConvertedRecipe(id: $id) {
      id
      ingredients_raw
    }
  }
`;

const RecipeConvert = () => {
  useIsomorphicStyles(RecipeConvertStyles);
  const dispatch = useDispatch();
  const contentId = useSelector(state => state.conversion.id);
  const prompt = useSelector(state => state.conversion.openAIPrompt);
  const model = useSelector(state => state.conversion.model);
  const hasConverted = useSelector(state => state.conversion.hasConverted);
  const convertedIngredients = useSelector(state => state.conversion.convertedIngredients);
  const allowedToConvertRecipe = useSelector(state => state.conversion.allowedToConvertRecipe);
  const allowedToEditConversion = useSelector(state => state.conversion.allowedToEditConversion);
  const canConvertRecipe = useSelector(state => state.conversion.canConvertRecipe);
  const conversionExists = useSelector(state => state.conversion.conversionExists);
  const currentMeasurementSystem = useSelector(state => state.conversion.currentMeasurementSystem);
  const isConversionEditOpen = useSelector(state => state.conversion.isConversionEditOpen);
  const userUnitSystem = useSelector(state => state.user.unit_system);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [convertedRecipe, setConvertedRecipe] = useState(convertedIngredients);
  const [convertRecipe] = useMutation(CONVERT_RECIPE_USING_QUEUE);
  const unitConversionEnabled = useSelector(state => state.conversion.unitConversionEnabled);

  const showConversion =
    (contentId && unitConversionEnabled) || (contentId && allowedToConvertRecipe);

  const initialQuery = useQuery(GET_CONVERTED_RECIPE, {
    skip: !showConversion,
    variables: { id: contentId },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (
        data?.GetConvertedRecipe?.ingredients_raw?.length > 0 &&
        data?.GetConvertedRecipe?.ingredients_raw[0] !== null
      ) {
        dispatch(
          setConvertedIngredients({
            ingredients: data.GetConvertedRecipe.ingredients_raw,
            withoutSwitching: true,
            userUnitSystem,
          }),
        );
      }
    },
  });

  const { startPolling, stopPolling } = useQuery(GET_CONVERTED_RECIPE, {
    variables: { id: contentId },
    skip: !submitting || convertedRecipe,
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (
        data?.GetConvertedRecipe?.ingredients_raw?.length > 0 &&
        data?.GetConvertedRecipe?.ingredients_raw[0] !== null
      ) {
        dispatch(setConvertedIngredients({ ingredients: data.GetConvertedRecipe.ingredients_raw }));
        setSubmitting(false);
        stopPolling();
        setError(false);
      }
    },
    onError: () => {
      setError(true);
      setSubmitting(false);
      stopPolling();
    },
  });

  useEffect(
    () => {
      if (contentId) {
        initialQuery.refetch();
      }
    },
    [contentId, initialQuery],
  );

  useEffect(
    () => {
      let timeout;
      if (submitting) {
        startPolling(1000);
        timeout = setTimeout(() => {
          if (submitting) {
            setError(true);
            setSubmitting(false);
            stopPolling();
          }
        }, CONVERT_TIMEOUT * 1000);
      }
      return () => {
        stopPolling();
        clearTimeout(timeout);
      };
    },
    [submitting, startPolling, stopPolling],
  );

  const initiateConversion = async () => {
    setError(false);
    setSubmitting(true);

    try {
      await convertRecipe({ variables: { id: contentId, prompt, model } });
    } catch (convertRecipeError) {
      setError(true);
      setSubmitting(false);
    }
  };

  // eslint-disable-next-line
  const handleChange = async () => {
    if (hasConverted) {
      return;
    }

    if (convertedIngredients !== null && convertedIngredients.length > 0) {
      setConvertedRecipe(convertedIngredients);
      setSubmitting(false);
      return;
    }

    try {
      const { data } = await initialQuery.refetch();
      if (
        data?.GetConvertedRecipe?.ingredients_raw?.length > 0 &&
        data?.GetConvertedRecipe?.ingredients_raw[0] !== null
      ) {
        setConvertedRecipe(data.GetConvertedRecipe.ingredients_raw);
      } else {
        initiateConversion();
      }
    } catch (initialQueryError) {
      initiateConversion();
    }
  };

  const handleChangeNoConvert = async () => {
    if (hasConverted || (convertedIngredients !== null && convertedIngredients.length > 0)) {
      dispatch(switchCurrentMeasurementSystem());
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await initialQuery.refetch();
      if (
        data?.GetConvertedRecipe?.ingredients_raw?.length > 0 &&
        data?.GetConvertedRecipe?.ingredients_raw[0] !== null
      ) {
        dispatch(setConvertedIngredients({ ingredients: data.GetConvertedRecipe.ingredients_raw }));
      }
      setSubmitting(false);
    } catch (initialQueryError) {
      setSubmitting(false);
    }
  };

  const tooltipText = useMemo(
    () => {
      if (!allowedToEditConversion) {
        return 'Set preferred units';
      }

      if (canConvertRecipe) {
        return 'Toggle conversion options';
      }

      return `Recipe cannot be converted. Measurement system is ${currentMeasurementSystem}.`;
    },
    [canConvertRecipe, currentMeasurementSystem, allowedToEditConversion],
  );

  const conversionExistsAndCanConvertRecipe = conversionExists && canConvertRecipe;

  const handleConvertToggle = () => {
    if (allowedToEditConversion && canConvertRecipe) {
      dispatch(toggleConversionEditOpen());

      return;
    }

    if (conversionExistsAndCanConvertRecipe) {
      history.push('/user/settings/units');
    }
  };

  if (!showConversion) {
    return null;
  }

  return (
    <div className="recipe-convert">
      <div className="recipe-convert__tooltips">
        {(allowedToEditConversion || conversionExistsAndCanConvertRecipe) && (
          <ResponsiveTooltip className="recipe-convert__info" title={tooltipText}>
            <button className="recipe-convert__toggle" onClick={handleConvertToggle}>
              <Icon
                fill={isConversionEditOpen ? COLOURS.ENERGIZED : COLOURS.SOLID_GREY}
                name="cog"
                height="22"
                width="22"
              />
            </button>
          </ResponsiveTooltip>
        )}
        {error && (
          <ResponsiveTooltip
            className="recipe-convert__info"
            title="Error occurred converting recipe. Please try Later"
          >
            <Icon fill={COLOURS.ERROR} name="error-outline" height="24" width="24" />
          </ResponsiveTooltip>
        )}
        {conversionExistsAndCanConvertRecipe && (
          <ResponsiveTooltip title="Automated conversion, for guidance only">
            <Icon fill={COLOURS.SOLID_GREY} name="info-outline" height="24" width="24" />
          </ResponsiveTooltip>
        )}
      </div>
      {error && <div className="recipe-convert__error">Please try later</div>}
      {conversionExistsAndCanConvertRecipe && (
        <TwoLabelSwitch
          disabled={submitting}
          checked={currentMeasurementSystem === 'onlyUs'}
          handleChange={handleChangeNoConvert}
          leftText="Metric"
          rightText="Cups"
        />
      )}
    </div>
  );
};

export default RecipeConvert;
